// components/LayerResizeHandle.tsx
import React, { useCallback, useMemo } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { resolveStudioLayerTransform } from '#lib/studio-layer-transform';

interface LayerResizeHandleProps {
  layerId: string;
  handleResizeHandleMouseDown: (event: React.MouseEvent, layerId: string, corner: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'top' | 'bottom' | 'left' | 'right') => void;
  corner: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'top' | 'bottom' | 'left' | 'right';
}

const LayerResizeHandle: React.FC<LayerResizeHandleProps> = React.memo(({ layerId, handleResizeHandleMouseDown, corner }) => {
  const inverseZoomLevelCssVar = 'var(--inverse-zoom-level, 1)';
  const layerExists = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId] != null);
  const layerWorkspaceId = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.workspaceId ?? '');
  const workspaceFrameRectX = useKernelStore((state) => {
    const workspaceId = state.inventory.StudioLayer.registry[layerId]?.workspaceId;
    return workspaceId ? state.inventory.StudioWorkspace.registry[workspaceId]?.frameRect?.x : undefined;
  });
  const workspaceFrameRectY = useKernelStore((state) => {
    const workspaceId = state.inventory.StudioLayer.registry[layerId]?.workspaceId;
    return workspaceId ? state.inventory.StudioWorkspace.registry[workspaceId]?.frameRect?.y : undefined;
  });
  const rectX = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.rect.x);
  const rectY = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.rect.y);
  const rectW = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.rect.w);
  const rectH = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.rect.h);
  const scale = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.scale ?? 1);
  const imageWidth = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.imageData?.width);
  const imageHeight = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.imageData?.height);
  const layerParams = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.params);
  
  const handleStyle = useMemo((): React.CSSProperties | null => {
    if (!layerExists) return null;
    if (!layerWorkspaceId) return null;
    if (typeof rectX !== 'number' || typeof rectY !== 'number') return null;
    if (typeof workspaceFrameRectX !== 'number' || typeof workspaceFrameRectY !== 'number') return null;

    const handleScreenSize = 10;

    const layerWorldX = workspaceFrameRectX + rectX;
    const layerWorldY = workspaceFrameRectY + rectY;

    let scaledWidth: number | null = null;
    let scaledHeight: number | null = null;
    if (typeof rectW === 'number' && Number.isFinite(rectW) && rectW > 0) {
      scaledWidth = rectW;
    }
    if (typeof rectH === 'number' && Number.isFinite(rectH) && rectH > 0) {
      scaledHeight = rectH;
    }
    if (scaledWidth === null || scaledHeight === null) {
      if (typeof imageWidth !== 'number' || typeof imageHeight !== 'number') return null;
      scaledWidth = imageWidth * scale;
      scaledHeight = imageHeight * scale;
    }

    let worldX = 0;
    let worldY = 0;
    let cursor = 'default';
    let width = handleScreenSize;
    let height = handleScreenSize;
    let borderRadius: string | number = '2px';

    switch (corner) {
      // Corners
      case 'topLeft':
        worldX = layerWorldX;
        worldY = layerWorldY;
        cursor = 'nwse-resize';
        break;
      case 'topRight':
        worldX = layerWorldX + scaledWidth;
        worldY = layerWorldY;
        cursor = 'nesw-resize';
        break;
      case 'bottomLeft':
        worldX = layerWorldX;
        worldY = layerWorldY + scaledHeight;
        cursor = 'nesw-resize';
        break;
      case 'bottomRight':
        worldX = layerWorldX + scaledWidth;
        worldY = layerWorldY + scaledHeight;
        cursor = 'nwse-resize';
        break;
      // Sides (mid-edge) — same small square as the corners.
      case 'top':
        worldX = layerWorldX + scaledWidth / 2;
        worldY = layerWorldY;
        cursor = 'ns-resize';
        break;
      case 'bottom':
        worldX = layerWorldX + scaledWidth / 2;
        worldY = layerWorldY + scaledHeight;
        cursor = 'ns-resize';
        break;
      case 'left':
        worldX = layerWorldX;
        worldY = layerWorldY + scaledHeight / 2;
        cursor = 'ew-resize';
        break;
      case 'right':
        worldX = layerWorldX + scaledWidth;
        worldY = layerWorldY + scaledHeight / 2;
        cursor = 'ew-resize';
        break;
    }

    const layerTransform = resolveStudioLayerTransform(layerParams);
    if (Math.abs(layerTransform.rotationDeg) > 0.0005) {
      const centerX = layerWorldX + scaledWidth / 2;
      const centerY = layerWorldY + scaledHeight / 2;
      const radians = layerTransform.rotationDeg * Math.PI / 180;
      const dx = worldX - centerX;
      const dy = worldY - centerY;
      worldX = centerX + dx * Math.cos(radians) - dy * Math.sin(radians);
      worldY = centerY + dx * Math.sin(radians) + dy * Math.cos(radians);
    }

    return {
      position: 'absolute' as const,
      // MODIFIED: Set top/left to 0 to create a stable origin for the transform.
      top: '0px',
      left: '0px',
      width: `${width}px`,
      height: `${height}px`,
      border: `1.5px solid #3B82F6`,
      backgroundColor: 'white',
      borderRadius: borderRadius,
      boxShadow: `0 0 2px rgba(0,0,0,0.5)`,
      cursor: cursor,
      pointerEvents: 'auto' as const,
      zIndex: 10,
      transform: `translateX(${worldX}px) translateY(${worldY}px) translate(-50%, -50%) scale(${inverseZoomLevelCssVar})`,
      transformOrigin: '50% 50%' as const,
    };
  }, [
    corner,
    imageHeight,
    imageWidth,
    inverseZoomLevelCssVar,
    layerParams,
    layerExists,
    layerWorkspaceId,
    rectH,
    rectW,
    rectX,
    rectY,
    scale,
    workspaceFrameRectX,
    workspaceFrameRectY,
  ]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    handleResizeHandleMouseDown(e, layerId, corner);
  }, [corner, handleResizeHandleMouseDown, layerId]);

  if (!handleStyle) {
    return null;
  }

  return (
    <div
      style={handleStyle}
      onMouseDown={handleMouseDown}
    />
  );
});

LayerResizeHandle.displayName = 'LayerResizeHandle';

export default LayerResizeHandle;
